import { ReactNode } from 'react';
import { graphql, readInlineData } from 'react-relay';

import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage, IntlShape } from 'dibs-react-intl';

import { CurrencyProvider } from '../../CurrencyProvider/CurrencyProvider';
import ExpiringPromoMessage from '../ExpiringPromoMessage';
import { shouldShowExpiringPromo } from './expiringPromoMessageHelpers';
import {
    messageItemTemplate,
    isLowResolution,
    getNoteByDevice,
    hasAnyText,
    getFallbackMessagesArray,
    MessageItem,
    MessageSet,
    MessageItemTemplateData,
    NoteObj,
    NoteByDeviceItem,
} from './sharedMessageHelpers';

import { messageHelpers_viewer$key } from './__generated__/messageHelpers_viewer.graphql';
import { messageHelpers_user$key } from './__generated__/messageHelpers_user.graphql';
import { messageHelpers_promotion$key } from './__generated__/messageHelpers_promotion.graphql';
import { messageHelpers_expiringPromotion$key } from './__generated__/messageHelpers_expiringPromotion.graphql';

type ImpressionCapsType = { [key: string]: number };

const LAST_BANNER_MESSAGE_ID = 'lastBannerMessageId';
const MESSAGE_IMPRESSION_CAPS = 'messageImpressionCaps';
const TERMS_URL = '/promotions/terms/?promo=';

const shownIds: { [key: string]: boolean } = {};

const viewerFragment = graphql`
    fragment messageHelpers_viewer on Viewer @inline {
        promoCodeMessaging: featureFlag(feature: "promoCodeMessaging")
    }
`;

const userFragment = graphql`
    fragment messageHelpers_user on User @inline {
        preferences {
            currency
        }
    }
`;

const promotionFragment = graphql`
    fragment messageHelpers_promotion on Promotion @inline {
        serviceId
        buyerDisplayable
        flatAdjustmentRate {
            rate {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        code
        promotionMeta {
            customBannerMessage
            desktopBannerMessage
            desktopBannerCta
            mobileBannerMessage
            mobileBannerCta
        }
    }
`;

const expiringPromotionFragment = graphql`
    fragment messageHelpers_expiringPromotion on Promotion @inline {
        ...ExpiringPromoMessage_promotion
        ...expiringPromoMessageHelpers_expiringPromotion
    }
`;

const getNoteId = (messageItem: MessageItem): string | null | undefined =>
    getNoteByDevice(messageItem)?.serviceId;

export const addToShownList = (messageItem: MessageItem): void => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        shownIds[noteId] = true;
    }
};

export const wasShown = (messageItem: MessageItem): boolean =>
    !!shownIds[getNoteId(messageItem) || ''];

const increaseImpressionCap = (noteId: string): void => {
    const msgImpressCaps =
        (localStorage.getItem(MESSAGE_IMPRESSION_CAPS) as ImpressionCapsType) || {};

    msgImpressCaps[noteId] = msgImpressCaps[noteId] ? msgImpressCaps[noteId] + 1 : 1;

    localStorage.setItem(MESSAGE_IMPRESSION_CAPS, msgImpressCaps);
};

const setNoteIdToStorage = (noteId: string): void => {
    localStorage.setItem(LAST_BANNER_MESSAGE_ID, noteId);
};

export const setValuesToStorage = (messageItem: MessageItem): void => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        increaseImpressionCap(noteId);
        setNoteIdToStorage(noteId);
    }
};

const validImpressionCap = (messageItem: MessageItem): boolean => {
    const noteId = getNoteId(messageItem) || '';
    const msgImpressCaps = localStorage.getItem(MESSAGE_IMPRESSION_CAPS) as ImpressionCapsType;
    if (msgImpressCaps && msgImpressCaps[noteId]) {
        return Number(messageItem?.impressionCap) > msgImpressCaps[noteId];
    }
    return true;
};

const getNextMessageIndex = (allMessages: MessageItem[], validMessages: MessageItem[]): number => {
    const lastMsgId = localStorage.getItem(LAST_BANNER_MESSAGE_ID);
    if (lastMsgId) {
        const lastMsgIndex = allMessages.findIndex(
            (item: MessageItem) => getNoteId(item) === lastMsgId.toString()
        );
        for (let i = 0; i < allMessages.length; i++) {
            const nextMsgItem = allMessages[(i + (lastMsgIndex + 1)) % allMessages.length];
            const indexInValidArray = validMessages.findIndex(
                (item: MessageItem) => getNoteId(item) === getNoteId(nextMsgItem)
            );
            if (indexInValidArray !== -1) {
                return indexInValidArray;
            }
        }
    }
    return 0;
};

const countAllMessages = (messages: MessageItem[]): number => {
    return messages.filter((item: MessageItem) => hasAnyText(item)).length || 0;
};

const getValidMessagesArray = (messages: MessageItem[]): MessageItem[] => {
    return messages.filter((item: MessageItem) => hasAnyText(item) && validImpressionCap(item));
};

const moveItemToFront = (messages: MessageItem[], index: number): MessageItem[] => {
    return messages.splice(index).concat(messages);
};

export const getIsFirstTimeVisitor = (): boolean => {
    const { totalVisitCount } =
        (localStorage.getItem('visitCount') as {
            totalVisitCount: number;
        }) || {};
    const { session } =
        (localStorage.getItem('pseudoSessionVisitCount') as {
            session: number;
        }) || {};

    return totalVisitCount <= session;
};

export const getMessagesToShow = ({
    intl,
    messageSet,
    useGlobalFallbackMessage,
}: {
    intl: IntlShape;
    messageSet: MessageSet;
    useGlobalFallbackMessage: boolean;
}): NoteObj[] | MessageItem[] => {
    if (!messageSet) {
        return useGlobalFallbackMessage ? getFallbackMessagesArray(intl, []) : [];
    }
    const firstMessageSet = messageSet?.[0]?.items || [];
    const secondMessageSet = messageSet?.[1]?.items || [];
    const allMessages = firstMessageSet.concat(secondMessageSet) as MessageItem[];
    const allMessagesCount = countAllMessages(allMessages);
    if (allMessagesCount) {
        const validMessages = getValidMessagesArray(allMessages);
        if (validMessages && validMessages.length) {
            return moveItemToFront(validMessages, getNextMessageIndex(allMessages, validMessages));
        }
    }
    return useGlobalFallbackMessage ? getFallbackMessagesArray(intl, messageSet) : [];
};

export const getPromoMessage = ({
    viewerRef,
    userRef,
    promotionRef,
    expiringPromotionRef,
    intl,
    defaultCurrency,
}: {
    viewerRef: messageHelpers_viewer$key;
    userRef: messageHelpers_user$key;
    promotionRef: messageHelpers_promotion$key;
    expiringPromotionRef: messageHelpers_expiringPromotion$key;
    intl: IntlShape;
    defaultCurrency: string;
}): NoteObj[] | NoteByDeviceItem[] | null => {
    const { promoCodeMessaging } = readInlineData(viewerFragment, viewerRef) || {};
    const userData = readInlineData(userFragment, userRef);
    const activePromotion = readInlineData(promotionFragment, promotionRef) || {};
    const expiringPromotion = readInlineData(expiringPromotionFragment, expiringPromotionRef);
    const { preferences } = userData || {};
    const { promotionMeta, code, flatAdjustmentRate, buyerDisplayable, serviceId } =
        activePromotion;
    const flatRate = flatAdjustmentRate?.rate || { convertedAmountList: [] };
    const {
        customBannerMessage,
        desktopBannerMessage,
        desktopBannerCta,
        mobileBannerMessage,
        mobileBannerCta,
    } = promotionMeta || {};

    if (promoCodeMessaging && buyerDisplayable) {
        const url = TERMS_URL + code;

        if (shouldShowExpiringPromo(expiringPromotion)) {
            const content = {
                title: 'expiring promotion',
                message: (
                    <CurrencyProvider
                        render={({ currency }: { currency: string }) => (
                            <ExpiringPromoMessage
                                promotion={expiringPromotion}
                                isLowResolution={isLowResolution}
                                currency={currency}
                            />
                        )}
                    />
                ) as ReactNode,
                cta: {
                    text: (
                        <FormattedMessage
                            id="dbl.Header.MessageBanner.expiringPromoCtaText"
                            defaultMessage="Details"
                        />
                    ) as ReactNode,
                    url,
                },
            } as MessageItemTemplateData;
            const message = messageItemTemplate({
                desktop: content,
                mobile: content,
            });
            return [message];
        } else if (desktopBannerMessage || mobileBannerMessage) {
            const updateBannerMessage = (message: string | null | undefined): string | null => {
                if (message && !customBannerMessage && flatAdjustmentRate) {
                    const currency = preferences?.currency || defaultCurrency || 'USD';
                    const amount =
                        (flatRate.convertedAmountList || []).find(c => c?.currency === currency)
                            ?.amount || 0;
                    message = message.replace(
                        /\{discount\}/,
                        intl.formatNumber(amount, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })
                    );
                }
                return message || null;
            };

            const message = {
                note: {
                    serviceId: `${serviceId}_desktop`,
                    content: {
                        title: code,
                        message: updateBannerMessage(desktopBannerMessage),
                        cta: {
                            text: desktopBannerCta,
                            url,
                        },
                    },
                    anchors: [
                        {
                            serviceId: `${serviceId}_mobile`,
                            content: {
                                title: code,
                                message: updateBannerMessage(mobileBannerMessage),
                                cta: {
                                    text: mobileBannerCta,
                                    url,
                                },
                            },
                        },
                    ],
                },
            };
            return hasAnyText(message) ? [message] : null;
        }
    }
    return null;
};
